import { useState, useEffect, useRef } from 'react';
import { useReducedMotion } from 'framer-motion';
import { SITE_DEFAULTS } from '../data/content';
import { PRERENDER } from '../lib/prerender';

const DEFAULT_TEAM = SITE_DEFAULTS.team;

const TeamSection = ({ teamContent }) => {
  // If teamContent prop isn't ready yet (Home.jsx loads async), fall back to defaults
  const TEAM_DATA = (Array.isArray(teamContent) && teamContent.length > 0) ? teamContent : DEFAULT_TEAM;
  const containerRef = useRef(null);
  const activeRef = useRef(null);
  const [progress, setProgress] = useState(0);
  const [activeBarberId, setActiveBarberId] = useState(TEAM_DATA[0].id);
  const [vw, setVw] = useState(typeof window !== 'undefined' ? window.innerWidth : 1200);
  const [vh, setVh] = useState(typeof window !== 'undefined' ? window.innerHeight : 900);
  const reducedMotion = useReducedMotion();

  const gold = '#C6A24A';
  const font = "'Oswald', sans-serif";
  const isMobile = vw < 768;
  const compact = vw < 900 || vh < 840 || reducedMotion;
  const activeBarber = TEAM_DATA.find(barber => barber.id === activeBarberId) || TEAM_DATA[0];

  const orbiters = TEAM_DATA.filter(b => b.id !== activeBarber.id);
  const selectors = compact ? TEAM_DATA : orbiters;
  const orbiterSize = compact ? 62 : 90;
  const radiusX = Math.min(360, (vw - orbiterSize - 64) / 2);
  const radiusY = Math.min(330, (vh - 220) / 2);
  const portraitHeight = compact ? Math.min(220, vh * 0.31) : Math.min(340, (vh - 220) / 2);

  useEffect(() => {
    const handleScroll = () => {
      if (!containerRef.current) return;
      const rect = containerRef.current.getBoundingClientRect();
      const scrolled = Math.max(0, -rect.top);
      const total = containerRef.current.offsetHeight - window.innerHeight;
      setProgress(Math.min(scrolled / Math.max(total, 1), 1));
    };
    const handleResize = () => {
      setVw(window.innerWidth);
      setVh(window.innerHeight);
      handleScroll();
    };
    handleScroll();
    window.addEventListener('scroll', handleScroll, { passive: true });
    window.addEventListener('resize', handleResize);
    return () => {
      window.removeEventListener('scroll', handleScroll);
      window.removeEventListener('resize', handleResize);
    };
  }, []);

  const visualProgress = compact || reducedMotion || PRERENDER ? 1 : progress;
  const headerOpacity = Math.min(visualProgress / 0.1, 1);
  const orbitProgress = Math.min(Math.max((visualProgress - 0.05) / 0.45, 0), 1);
  const easeProgress = 1 - Math.pow(1 - orbitProgress, 3);

  return (
    <section id="team" className="crew-section" aria-label="Our team" ref={containerRef} style={{ height: compact || reducedMotion ? 'auto' : '220vh', background: '#000', position: 'relative', overflow: 'clip' }}>
      
      {/* Background Image - Absolute so it stays in this section */}
      <div style={{
        position: 'absolute', top: 0, left: 0, width: '100%', height: '100%',
        backgroundImage: `url('https://brocode.b-cdn.net/DSC00401.JPG')`,
        backgroundSize: 'cover', backgroundPosition: 'center',
        backgroundAttachment: compact || reducedMotion ? 'scroll' : 'fixed',
        opacity: 0.6, zIndex: 0
      }} />
      
      {/* Dark Overlay - Absolute so it doesn't darken the rest of the site */}
      <div style={{
  position: 'absolute',
  top: 0, left: 0,
  width: '100%', height: '100%',
  background: 'rgba(0, 0, 0, 0.5)',
  backgroundSize: 'cover', backgroundPosition: 'center',
  backgroundAttachment: isMobile ? 'scroll' : 'fixed',
  opacity: 0.3, zIndex: 0
}} />

      <div style={{ position: compact ? 'relative' : 'sticky', top: 0, height: compact ? 'auto' : '100vh', minHeight: compact ? '100svh' : undefined, padding: compact ? '1.5rem 0' : 0, gap: compact ? '1rem' : 0, width: '100%', overflow: 'hidden', display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', zIndex: 2 }}>
        
        {/* Editorial Header */}
        <div style={{
          position: compact ? 'relative' : 'absolute',
          top: compact ? 'auto' : 'max(5%, 5rem)',
          left: compact ? 'auto' : '5.5rem',
          textAlign: compact ? 'center' : 'left',
          zIndex: 10,
          opacity: headerOpacity, 
          transform: `translateX(${(1 - headerOpacity) * -20}px)`,
        }}>
          <p style={{ color: gold, letterSpacing: '0.15em', fontSize: '0.65rem', fontWeight: 600, margin: '0 0 0.4rem' }}>
            Choose a barber to view their profile
          </p>
          <h3 style={{ color: '#fff', fontFamily: font, fontSize: 'clamp(2.5rem, 6vw, 4.5rem)', letterSpacing: '0.1em', margin: 0, lineHeight: 1 }}>
            THE CREW
          </h3>
          <div style={{ width: '40px', height: '2px', background: gold, margin: compact ? '1rem auto 0' : '1rem 0 0', opacity: 0.8 }} />
        </div>

        {/* The Orbit Container */}
        <div className="crew-stage" style={{
          position: 'relative', 
          width: '100%',
          height: compact ? 'auto' : '100%',
          display: compact ? 'flex' : 'block',
          flexDirection: 'column', alignItems: 'center', gap: '1rem',
        }}>
          
          {/* Active Central Barber Card */}
          <div className="crew-active" ref={activeRef} tabIndex={-1} aria-label={`${activeBarber.name}'s profile`} style={{
            position: compact ? 'relative' : 'absolute', top: compact ? 'auto' : '55%', left: compact ? 'auto' : '50%',
            transform: compact ? 'none' : `translate(-50%, -75%) scale(${0.85 + (easeProgress * 0.15)})`,
            width: portraitHeight * 0.75,
            height: portraitHeight,
            marginBottom: compact ? '110px' : 0,
            zIndex: 5, 
            opacity: easeProgress,
            transition: reducedMotion ? 'none' : 'opacity 0.6s ease, transform 0.6s cubic-bezier(0.23, 1, 0.32, 1)',
          }}>
            <div style={{ 
              width: '100%', height: '100%', 
              background: 'rgba(10, 10, 10, 0.9)',
              backdropFilter: 'blur(20px)',
              border: `1px solid rgba(198, 162, 74, 0.4)`, 
              padding: '6px',
              boxShadow: '0 50px 100px rgba(0,0,0,0.9)'
            }}>
              <img
                className="crew-photo"
                key={activeBarber.id}
                src={activeBarber.img}
                alt={`${activeBarber.name} — ${activeBarber.role} at Bro Code Barbershop`}
                loading="lazy"
                decoding="async"
                style={{
                  width: '100%', height: '100%', objectFit: 'cover',
                  objectPosition: activeBarber.imgPosition,
                  filter: 'grayscale(0.1) contrast(1.1)',
                  animation: 'crewFadeIn 0.8s ease-out'
                }}
              />
              
              <div className="crew-copy" aria-live="polite" style={{
                position: 'absolute', top: 'calc(100% + 1rem)',
                left: '50%', transform: 'translateX(-50%)', width: 'min(280px, calc(100vw - 48px))', textAlign: 'center'
              }}>
                <h4 style={{ color: '#fff', fontFamily: font, fontSize: isMobile ? '1.3rem' : '1.8rem', margin: 0 }}>
                  {activeBarber.name}
                </h4>
                <p style={{ color: gold, fontSize: '0.65rem', letterSpacing: '0.35em', margin: '4px 0 12px' }}>
                  {activeBarber.role}
                </p>
                {activeBarber.bio && <p style={{ 
                  color: 'rgba(255,255,255,0.8)', fontSize: compact ? '0.7rem' : '0.85rem',
                  lineHeight: 1.6, maxWidth: '280px', margin: '0 auto', fontFamily: 'serif', fontStyle: 'italic' 
                }}>
                   "{activeBarber.bio}"
                </p>}
              </div>
            </div>
          </div>

          {/* Orbiting Barbers */}
          <div className="crew-selectors" role="group" aria-label="Choose a team member" style={compact ? { display: 'flex', gap: '0.75rem', width: 'calc(100% - 3rem)', overflowX: 'auto', padding: '0.75rem 0.5rem', scrollSnapType: 'x proximity' } : { position: 'absolute', inset: 0 }}>
          {selectors.map((barber, i) => {
            const angle = (i * (360 / selectors.length) - 90) * (Math.PI / 180);
            const x = Math.cos(angle) * radiusX * easeProgress;
            const y = Math.sin(angle) * radiusY * easeProgress;

            return (
              <button
                className="crew-orbiter"
                key={barber.id}
                type="button"
                aria-label={`View ${barber.name}'s profile`}
                aria-pressed={activeBarber.id === barber.id}
                onClick={() => {
                  setActiveBarberId(barber.id);
                  if (!compact) activeRef.current?.focus({ preventScroll: true });
                }}
                style={{
                  position: compact ? 'relative' : 'absolute', top: compact ? 'auto' : '55%', left: compact ? 'auto' : '50%',
                  transform: compact ? 'none' : `translate(calc(-50% + ${x}px), calc(-50% + ${y}px))`,
                  width: orbiterSize,
                  height: orbiterSize,
                  flexShrink: 0, scrollSnapAlign: 'center',
                  borderRadius: '50%', 
                  cursor: 'pointer',
                  zIndex: 6, overflow: 'hidden', opacity: easeProgress,
                  background: '#000', padding: 0,
                }}
              >
                <img src={barber.img} loading="lazy" decoding="async" style={{ width: '100%', height: '100%', objectFit: 'cover', objectPosition: barber.imgPosition }} alt={barber.name} />
              </button>
            );
          })}
          </div>
        </div>
      </div>

      <style>{`
        .crew-orbiter {
          border: 1px solid rgba(198, 162, 74, 0.35);
          filter: grayscale(1) brightness(0.7);
          transition: transform 0.4s cubic-bezier(0.165, 0.84, 0.44, 1), filter 0.2s ease, border-color 0.2s ease;
        }
        .crew-orbiter:hover, .crew-orbiter:focus-visible, .crew-orbiter[aria-pressed="true"] {
          filter: grayscale(0) brightness(1.1);
          border-color: #C6A24A;
        }
        .crew-orbiter:focus-visible, .crew-active:focus-visible { outline: 2px solid #C6A24A; outline-offset: 4px; }
        @media (prefers-reduced-motion: reduce) {
          .crew-photo { animation: none !important; }
          .crew-orbiter { transition: none; }
        }
        @keyframes crewFadeIn {
          from { opacity: 0; filter: blur(20px) brightness(0); }
          to { opacity: 1; filter: blur(0) brightness(1); }
        }
      `}</style>
    </section>
  );
};

export default TeamSection;
